import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, FileText, RefreshCw } from "lucide-react";
import { jobsApi } from "./api";
import type { JobApplication, ListFilters } from "./types";
import { StatusBadge } from "./shared/StatusBadge";
import { FiltersBar } from "./shared/FiltersBar";
import { ApplicationsTable, type Column } from "./shared/ApplicationsTable";
import { ApplicationDrawer } from "./shared/ApplicationDrawer";
import { Section, Field, Link } from "./shared/InfoBlock";
import { formatDate, formatDateTime } from "./shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { useDebounce } from "@/lib/utils/useDebounce";
import { confirmAction } from "@/app/providers/ConfirmDialog";

const STATUS_OPTIONS = [
  { value: "new", label: "Nueva" },
  { value: "contacted", label: "Contactado" },
  { value: "archived", label: "Archivado" },
];

const PAGE_SIZE = 25;

const AREA_LABELS: Record<string, string> = {
  tech: "Tecnología",
  design: "Diseño",
  product: "Producto",
  marketing: "Marketing",
  content: "Contenido",
  community: "Comunidad",
  otro: "Otro",
};

export function JobsScreen() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => setPage(1), [debouncedSearch, status]);

  const filters: ListFilters = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      status: status || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [debouncedSearch, status, page],
  );

  const listQuery = useQuery({
    queryKey: ["applications", "jobs", filters],
    queryFn: () => jobsApi.list(filters),
    placeholderData: (prev) => prev,
  });

  const detailQuery = useQuery({
    queryKey: ["applications", "jobs", "detail", selectedId],
    queryFn: () => jobsApi.detail(selectedId!),
    enabled: selectedId !== null,
  });

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Aplicaciones · Trabaja con nosotros</p>
          <h1 className="nx-h1">Postulaciones de trabajo</h1>
          <p className="text-sm text-fg-muted mt-1">
            Bandeja de candidatos para sumarse al equipo Mixcore. Las nuevas postulaciones también disparan email a soporte.
          </p>
        </div>
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nombre, email o contenido…"
        status={status}
        onStatusChange={setStatus}
        statusOptions={STATUS_OPTIONS}
      />

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando postulaciones…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<JobApplication>
          rows={listQuery.data?.applications ?? []}
          columns={columns}
          page={listQuery.data?.page ?? 1}
          pageSize={listQuery.data?.pageSize ?? PAGE_SIZE}
          total={listQuery.data?.total ?? 0}
          onPageChange={setPage}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
        />
      )}

      <JobDrawer
        selectedId={selectedId}
        data={detailQuery.data ?? null}
        loading={detailQuery.isLoading && selectedId !== null}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

const columns: Column<JobApplication>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "applicant",
    header: "Postulante",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.fullName}</div>
        <div className="text-2xs text-fg-subtle break-all">{r.email}</div>
      </div>
    ),
  },
  {
    key: "area",
    header: "Área",
    cell: (r) => (
      <span className="text-fg-muted text-xs">{AREA_LABELS[r.area] ?? r.area}</span>
    ),
  },
  {
    key: "story",
    header: "Historia",
    cell: (r) => (
      <div className="text-fg-muted text-xs line-clamp-2 max-w-md">
        {r.story.slice(0, 140)}
        {r.story.length > 140 ? "…" : ""}
      </div>
    ),
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => <StatusBadge status={r.status} />,
    width: "min",
  },
  {
    key: "createdAt",
    header: "Recibida",
    cell: (r) => <span className="text-fg-subtle text-xs">{formatDate(r.createdAt)}</span>,
    width: "min",
    align: "right",
  },
];

function JobDrawer({
  selectedId,
  data,
  loading,
  onClose,
}: {
  selectedId: number | null;
  data: JobApplication | null;
  loading: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToast();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["applications", "jobs"] });
  };

  const updateNotes = useMutation({
    mutationFn: (notes: string | null) =>
      jobsApi.update(selectedId!, { internalNotes: notes }),
    onSuccess: () => {
      push({ kind: "success", message: "Notas guardadas." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudieron guardar las notas." }),
  });

  const updateStatus = useMutation({
    mutationFn: (newStatus: string) => jobsApi.update(selectedId!, { status: newStatus }),
    onSuccess: () => {
      push({ kind: "success", message: "Estado actualizado." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo actualizar el estado." }),
  });

  return (
    <ApplicationDrawer<JobApplication>
      open={selectedId !== null}
      data={data}
      loading={loading}
      title={data ? data.fullName : "Postulante"}
      subtitle={data ? `#${data.id} · ${formatDateTime(data.createdAt)}` : undefined}
      onClose={onClose}
      onSaveNotes={(notes) => updateNotes.mutateAsync(notes).then(() => undefined)}
      renderBody={(d) => (
        <>
          <div className="flex items-center gap-2"><StatusBadge status={d.status} /></div>

          <Section title="Contacto">
            <Field label="Nombre completo" value={d.fullName} wide />
            <Field
              label="Email"
              value={
                <a className="text-accent hover:underline" href={`mailto:${d.email}`}>
                  {d.email}
                </a>
              }
              wide
            />
            <Field label="Área" value={AREA_LABELS[d.area] ?? d.area} />
          </Section>

          <Section title="Historia / motivación">
            <Field
              label="Texto del postulante"
              value={<p className="whitespace-pre-wrap text-fg">{d.story}</p>}
              wide
            />
          </Section>

          <Section title="Links">
            <Field
              label="CV"
              value={
                d.cvUrl ? (
                  <a
                    href={d.cvUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-accent hover:underline"
                  >
                    <FileText size={12} /> Ver CV
                  </a>
                ) : (
                  "—"
                )
              }
              wide
            />
            <Field
              label="LinkedIn"
              value={d.linkedinUrl ? <Link href={d.linkedinUrl}>{d.linkedinUrl}</Link> : "—"}
              wide
            />
            <Field
              label="Instagram"
              value={d.instagramUrl ? <Link href={d.instagramUrl}>{d.instagramUrl}</Link> : "—"}
              wide
            />
            <Field
              label="SoundCloud"
              value={d.soundcloudUrl ? <Link href={d.soundcloudUrl}>{d.soundcloudUrl}</Link> : "—"}
              wide
            />
          </Section>
        </>
      )}
      renderActions={(d) => (
        <>
          {d.status !== "contacted" && (
            <button
              onClick={() => updateStatus.mutate("contacted")}
              disabled={updateStatus.isPending}
              className="nx-btn-secondary"
            >
              Marcar contactado
            </button>
          )}
          {d.status !== "archived" && (
            <button
              onClick={async () => {
                const ok = await confirmAction({
                  title: "Archivar postulación",
                  message: "Queda fuera de la bandeja activa. Podés volver a abrirla cambiando el filtro.",
                  confirmLabel: "Archivar",
                });
                if (ok === true) updateStatus.mutate("archived");
              }}
              disabled={updateStatus.isPending}
              className="nx-btn-secondary"
            >
              <Archive size={14} /> Archivar
            </button>
          )}
          {d.status === "archived" && (
            <button
              onClick={() => updateStatus.mutate("new")}
              disabled={updateStatus.isPending}
              className="nx-btn-secondary"
            >
              Reabrir
            </button>
          )}
        </>
      )}
    />
  );
}
